import type { SimUnit, SimuladorDynamicStructure, VehicleModel, VehicleVersion } from './types';

export type { SimuladorDynamicStructure };

/* =====================================================================
   Converte a `dynamicStructure` (mesma resolvida pelo builder e pelo
   renderer) em `VehicleModel[]` + `SimUnit[]`. Binding self-managed:
   a coleção de modelos é escolhida no painel (ContentTypeSelect) e as
   versões vêm de uma relação ONE_TO_MANY do modelo; as unidades vêm do
   canal.

   Resolução de campos: primeiro por TIPO via `_metadata` (image → foto,
   price → preço, array_string → specs), que independe do nome
   dado ao campo no site; convenção de nomes (`imagem`, `imagens360`,
   `preco`…) fica como fallback. `_metadata` vem porque `useDynamicStructure`
   usa `includeMetadata: true` (idem nos itens de relação).
   ===================================================================== */

type Loose = Record<string, unknown>;

interface FieldMeta {
  id?: string;
  name?: string;
  type?: string;
}

interface DynamicRelationBucket {
  contentTypeName?: string;
  contentTypeSlug?: string;
  items?: Loose[];
}

const metaFields = (entry: Loose): FieldMeta[] =>
  (entry._metadata as { fields?: FieldMeta[] } | undefined)?.fields ?? [];

/** Valor do primeiro campo do `type` informado, descoberto via `_metadata`. */
const byType = (entry: Loose, type: string): unknown => {
  const name = metaFields(entry).find((f) => f.type === type)?.name;
  return name ? entry[name] : undefined;
};

const norm = (s: string): string => s.toLowerCase();

// Campos de imagem que NÃO são a foto do veículo (banner, logo, ícone, fundo…).
const IMAGE_AVOID = /(banner|logo|icone|icon|fundo|background|selo|bandeira|marca)/;
// Nomes que indicam a foto do modelo/versão (preferidos).
const IMAGE_PREFER = /(foto|imagem|image|photo|capa|cover|principal|modelo|veic|carro)/;
// A "Imagem Principal" designada (foto oficial do veículo) e as secundárias.
const IMAGE_MAIN = /(principal|profile)/;
const IMAGE_ALT = /(alternativ|secundari)/;

/** Pontua um campo de imagem pelo NOME (determinístico, independe da ordem):
    Imagem Principal > outra foto > alternativa/secundária; banner/logo = 0. */
const imageScore = (name: string): number => {
  if (IMAGE_AVOID.test(name)) return 0;
  let score = IMAGE_PREFER.test(name) ? 2 : 1;
  if (IMAGE_MAIN.test(name)) score = 4;
  if (IMAGE_ALT.test(name)) score -= 1;
  return score;
};

/** Escolhe a MELHOR imagem entre os campos `image` do registro de forma
    DETERMINÍSTICA — maior score vence, empate desempata por nome. Assim o
    builder (ordem por `order` do campo) e o publicado (ordem do `_metadata` do
    OpenSearch) resolvem SEMPRE a mesma imagem (a Imagem Principal). `fallback`
    cobre registros sem `_metadata` (mock/testes). */
const bestImage = (entry: Loose, fallback: unknown): string => {
  const candidates = metaFields(entry)
    .filter((f) => f.type === 'image' && f.name)
    .map((f) => ({ name: norm(f.name as string), url: pickImage(entry[f.name as string]) }))
    .filter((c) => c.url);

  if (candidates.length) {
    const ranked = [...candidates].sort(
      (a, b) => imageScore(b.name) - imageScore(a.name) || a.name.localeCompare(b.name),
    );
    // Se só sobrou banner/logo (score 0), ainda é melhor mostrar algo.
    return ranked[0].url;
  }
  return pickImage(fallback);
};

const ROTATION_NAME = /(360|frames|giro|rotation)/;

const bestGallery = (entry: Loose, fallback: unknown): string[] => {
  const candidate = metaFields(entry).find(
    (f) => f.name && ROTATION_NAME.test(norm(f.name)) && Array.isArray(entry[f.name]),
  );

  if (candidate?.name) {
    const frames = pickGallery(entry[candidate.name]);
    if (frames.length) return frames;
  }
  return pickGallery(fallback);
};

const first = <T,>(...vals: T[]): T | undefined => vals.find((v) => v != null && v !== '');

const onlyDigits = (v: string): string => v.replace(/\D/g, '');

/** Aceita number | "219990" | "R$ 219.990,00" e devolve número. */
const toNumber = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const cents = /,\d{2}\s*$/.test(v); // formato BRL com centavos
    const digits = onlyDigits(v);
    if (!digits) return 0;
    const n = Number(digits);
    return cents ? n / 100 : n;
  }
  return 0;
};

const toStr = (v: unknown): string => {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
};

/** Extrai a URL de um campo de imagem. Alinhado ao getImageSrc canônico:
    string | {src} | {url} | {props:{src}} | {desktop:{props:{src}}} | {cover} | array. */
const pickImage = (v: unknown): string => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return pickImage(v[0]);
  if (typeof v === 'object') {
    const o = v as Loose;
    const desktop = o.desktop as Loose | undefined;
    if (desktop) {
      const dp = desktop.props as Loose | undefined;
      if (typeof dp?.src === 'string') return dp.src;
      if (typeof desktop.src === 'string') return desktop.src;
    }
    const props = o.props as Loose | undefined;
    if (typeof props?.src === 'string') return props.src;
    if (typeof o.src === 'string') return o.src;
    if (typeof o.url === 'string') return o.url;
    if (o.cover) return pickImage(o.cover);
    if (o.gallery) return pickImage(o.gallery);
  }
  return '';
};

/** Normaliza um campo de galeria/360 em string[] de URLs. */
const pickGallery = (v: unknown): string[] => {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(pickImage).filter(Boolean);
  if (typeof v === 'object') {
    const o = v as Loose;
    if (Array.isArray(o.gallery)) return o.gallery.map(pickImage).filter(Boolean);
    const single = pickImage(o);
    return single ? [single] : [];
  }
  if (typeof v === 'string') {
    const t = v.trim();
    if (t.startsWith('[')) {
      try {
        const arr = JSON.parse(t);
        if (Array.isArray(arr)) return arr.map(pickImage).filter(Boolean);
      } catch {
        /* ignore */
      }
    }
    return t.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

/** Normaliza specs/itens em string[]. */
const toSpecs = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map((x) => toStr(x)).filter(Boolean);
  if (typeof v === 'string') {
    const t = v.trim();
    if (t.startsWith('[')) {
      try {
        const arr = JSON.parse(t);
        if (Array.isArray(arr)) return arr.map((x) => toStr(x)).filter(Boolean);
      } catch {
        /* ignore */
      }
    }
    return t.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const idFrom = (e: Loose, idx: number, prefix: string): string =>
  toStr(first(e.slug, e.id, e._id)) || `${prefix}-${idx}`;

const mapVersion = (e: Loose, idx: number): VehicleVersion => ({
  id: idFrom(e, idx, 'version'),
  name: toStr(first(e.nome, e.name, e.versao, e.titulo) ?? byType(e, 'text')) || 'Versão',
  price: toNumber(byType(e, 'price') ?? first(e.preco, e.price, e.valor, e.valorapartirde)),
  specs: toSpecs(byType(e, 'array_string') ?? first(e.specs, e.itens, e.destaques)),
  image: bestImage(e, first(e.imagem, e.image, e.foto, e.profile_image, e.thumbnail)) || undefined,
  // 360 da versão (ex.: rotation_gallery / "Galeria 360").
  frames360: bestGallery(e, first(e.imagens360, e.frames360, e.galeria360, e.rotation_gallery, e['360'])),
});

const relationItems = (entry: Loose): Loose[] => {
  const relations = entry.relations as Record<string, DynamicRelationBucket> | undefined;
  if (!relations || typeof relations !== 'object') return [];
  return Object.values(relations).flatMap((bucket) => (Array.isArray(bucket?.items) ? bucket.items : []));
};

const mapModel = (e: Loose, idx: number): VehicleModel => ({
  id: idFrom(e, idx, 'model'),
  name: toStr(first(e.nome, e.name, e.modelo, e.titulo) ?? byType(e, 'text')) || 'Modelo',
  image: bestImage(e, first(e.imagem, e.image, e.foto, e.capa)),
  frames360: bestGallery(e, first(e.imagens360, e.frames360, e.galeria360, e['360'])),
  versions: relationItems(e).map(mapVersion),
});

export const mapVehicles = (section: unknown): VehicleModel[] => {
  if (!Array.isArray(section)) return [];
  return section
    .filter((e): e is Loose => !!e && typeof e === 'object')
    .map(mapModel)
    // um modelo sem versões não é simulável — descarta (fallback assume no wrapper)
    .filter((m) => m.versions.length > 0);
};

/** `operatingHours` do canal é um array [{dayOfWeek, hours}] (texto livre);
    o painel exibe uma linha só, então juntamos "dia: horário" por " · ". */
const formatHours = (v: unknown): string => {
  if (Array.isArray(v)) {
    return v
      .map((h) => {
        const o = (h ?? {}) as Loose;
        return [toStr(o.dayOfWeek), toStr(o.hours)].filter(Boolean).join(': ');
      })
      .filter(Boolean)
      .join(' · ');
  }
  return toStr(v);
};

export const mapUnits = (units: unknown): SimUnit[] => {
  if (!Array.isArray(units)) return [];
  return units
    .filter((u): u is Loose => !!u && typeof u === 'object')
    .map((u, idx) => ({
      id: toStr(first(u.id, u._id)) || `unit-${idx}`,
      name: toStr(first(u.displayName, u.name)) || 'Unidade',
      whatsapp: onlyDigits(toStr(first(u.phoneNumber, u.whatsapp))),
      phone: toStr(first(u.phoneNumber, u.phone)) || undefined,
      address: toStr(u.address) || undefined,
      openingHours: formatHours(first(u.operatingHours, u.openingHours)) || undefined,
      mapLink: toStr(u.mapLink) || undefined,
    }));
};

export const mapEntries = (
  structure?: SimuladorDynamicStructure | null,
): { vehicles: VehicleModel[]; units: SimUnit[] } => ({
  vehicles: mapVehicles(structure?.section),
  units: mapUnits(structure?.channel?.units),
});
